import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import ErrorDisplay from "../../components/ErrorDisplay/ErrorDisplay";
import { Headings } from "../../components/Headings";
import LoadingMask from "../../components/LoadingMask/LoadingMask";
import PageLayout from "../../components/PageLayout/PageLayout";
import PhoneLayout from "../../components/PhoneLayout/PhoneLayout";
import UserCheckInHistory from "../../components/UserCheckInHistory/UserCheckInHistory";
import UserLikedPlaces from "../../components/UserLikedPlaces/UserLikedPlaces";
import { useAppAuthState } from "../../hooks/useAppAuthState";
import { useUser } from "../../hooks/useUser";
import { useUserProfile } from "../../hooks/useUserProfile";
import { SitePage } from "../../types/SitePage";
import { formatDate } from "../../utils/format-date";
import ProfileImageUpdate from "./ProfileImageUpdate";
import "./ProfilePage.scss";
import SocialsUpdateForm from "./SocialsUpdateForm";
import UsernameUpdateForm from "./UsernameUpdateForm";
import ZipCodeUpdateForm from "./ZipCodeUpdateForm";

interface ProfilePageProps {}

const ProfilePage: React.FC<ProfilePageProps> = () => {
  const navigate = useNavigate();
  const { user: firebaseUser, isAuthLoading } = useAppAuthState();
  const { user } = useUser(firebaseUser?.uid);
  const {
    profile,
    loading: profileLoading,
    error,
  } = useUserProfile(firebaseUser?.uid);
  const [errorUpdating, setErrorUpdating] = useState<string | null>(null);

  // Navigate to onboarding if user is logged in but has no profile
  useEffect(() => {
    if (!firebaseUser && !isAuthLoading) {
      navigate("/");
    }

    if (firebaseUser && !profile && !profileLoading) {
      navigate("/map?onboarding=true");
    }
  }, [firebaseUser, profile, isAuthLoading, profileLoading, navigate]);

  const handleUpdateError = useCallback((error: Error) => {
    setErrorUpdating(error.message);
  }, []);

  const pageContent = useMemo(() => {
    if (profileLoading) {
      return null;
    }

    if (error && !profile) {
      return (
        <div className="profile-page-container">
          <ErrorDisplay error={error} />
        </div>
      );
    }

    if (!firebaseUser || !profile) {
      return (
        <div className="profile-page-container">
          <div className="alert alert-warning">
            Please log in to view your profile.
          </div>
        </div>
      );
    }

    return (
      <div className="profile-page-container">
        <Headings.PhoneMain>
          {!!profile.username ? (
            <span>{profile.username}</span>
          ) : (
            <span>User Profile</span>
          )}
        </Headings.PhoneMain>
        <div className="profile-subheading">
          {formatDate(profile.createdAt)} &middot; {firebaseUser.email}
          {!!profile.username && (
            <Link
              className="view-profile-link"
              to={`/player/${profile.username}`}
            >
              View Public Profile
            </Link>
          )}
        </div>

        {errorUpdating && (
          <div className="alert alert-danger">{errorUpdating}</div>
        )}

        <ProfileImageUpdate
          userUid={firebaseUser.uid}
          profileImageUrl={profile.photoURL || null}
          onError={handleUpdateError}
          onClearError={() => setErrorUpdating(null)}
        />

        <UserLikedPlaces userId={firebaseUser.uid} />
        <UserCheckInHistory userId={firebaseUser.uid} />

        <div className="profile-details">
          <UsernameUpdateForm
            user={user}
            userUid={firebaseUser.uid}
            username={profile.username}
            onError={handleUpdateError}
            onClearError={() => setErrorUpdating(null)}
          />
        </div>
        <div className="profile-details">
          <SocialsUpdateForm
            userUid={firebaseUser.uid}
            socials={profile.socials ?? []}
            onError={handleUpdateError}
            onClearError={() => setErrorUpdating(null)}
          />
        </div>
        <div className="profile-details">
          <ZipCodeUpdateForm
            userUid={firebaseUser.uid}
            zipCode={profile.zipCode || null}
            onError={handleUpdateError}
            onClearError={() => setErrorUpdating(null)}
          />
        </div>
      </div>
    );
  }, [
    profile,
    profileLoading,
    error,
    user,
    firebaseUser,
    errorUpdating,
    handleUpdateError,
  ]);

  if (isAuthLoading || profileLoading) {
    return <LoadingMask message="Loading profile..." />;
  }

  return (
    <PageLayout page={SitePage.Profile} title="Profile">
      <PhoneLayout>{pageContent}</PhoneLayout>
    </PageLayout>
  );
};

export default ProfilePage;
